import "./styles.less";

import React, { FC, useContext } from "react";
import { Skeleton, Space, Typography } from "antd";

import { BathroomsTag } from "./BathroomsTag/web";
import { BedroomsTag } from "./BedroomsTag/web";
import { GuestsTag } from "./GuestsTag/web";
import { M2Tag } from "./M2Tag/web";
import { RoomsTag } from "./RoomsTag/web";
import { useFilters } from "../../Filters/Filters.hook";
import { useTheme } from "../../../Styles/ThemeHook";
import { useTypologyTag } from "./TypologyTag.hook";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";

export interface TypologyTagProps {
	propertyType: string;
	operationType: string;
	bedrooms?: number | [number];
	bathrooms?: number | [number];
	rooms?: number | [number];
	m2?: number;
	hectares?: number;
	guests?: number;
	loading: boolean;
	showIcons?: boolean;
	className?: string;
	isEspecial?: Boolean;
}

export const TypologyTag: FC<TypologyTagProps> = ({
	propertyType,
	operationType,
	bedrooms,
	rooms,
	bathrooms,
	m2,
	hectares,
	guests,
	loading,
	showIcons = false,
	className = "",
	isEspecial,
}) => {
	const { showBathrooms, showBedrooms, showM2, showRooms, showGuests } = useTypologyTag({
		propertyType,
		operationType
	});
	const { theme } = useTheme();
	const { country_code } = useContext(ConfigStateContext);
	const { filters } = useFilters();

	if (loading) return <TypologyTagSkeleton className={className} />;

	const separador = !showIcons ? <Typography.Text>·</Typography.Text> : null;

	const getMinBedroom = bedrooms => {
		if (Array.isArray(bedrooms)) {
			if (filters.bedrooms && filters.bedrooms[0]) {
				for (const rooms of bedrooms) {
					if (rooms >= filters.bedrooms[0]) {
						return rooms;
					}
				}
			} else {
				return bedrooms[0];
			}
		}

		return bedrooms;
	};

	const getMinRoom = rooms => {
		if (Array.isArray(rooms)) {
			if (filters.rooms && filters.rooms[0]) {
				for (const room of rooms) {
					if (room >= filters.rooms[0]) {
						return room;
					}
				}
			} else {
				return rooms[0];
			}
		}

		return rooms;
	};

	const getMinBathooms = bathrooms => {
		if (Array.isArray(bathrooms)) {
			if (filters.bathrooms && filters.bathrooms[0]) {
				for (const bathroom of bathrooms) {
					if (bathroom >= filters.bathrooms[0]) {
						return bathroom;
					}
				}
			} else {
				return bathrooms[0];
			}
		}

		return bathrooms;
	};

	const minBedrooms = getMinBedroom(bedrooms);
	const minRooms = getMinRoom(rooms);
	const hideZeroBedrooms = country_code === "CO" && Number(minBedrooms) === 0;
	const hideZeroRooms = Number(minRooms) === 0;
	const shouldShowBedroomsTag = showBedrooms && !hideZeroBedrooms;
	const shouldShowRoomsTag = showRooms && !hideZeroRooms;

	return (
		<div
			className={
				"property-typology-tag" + " " + className + (showIcons ? " show-icons" : "")
			}>
			{shouldShowBedroomsTag && (
				<BedroomsTag
					isEspecial={isEspecial}
					bedrooms={minBedrooms}
					icon={showIcons}
					prefix={Array.isArray(bedrooms) && bedrooms.length > 1}
				/>
			)}

			{shouldShowRoomsTag && shouldShowBedroomsTag && separador}
			{shouldShowRoomsTag && (
				<RoomsTag
					isEspecial={isEspecial}
					rooms={minRooms}
					icon={showIcons}
					prefix={Array.isArray(rooms) && rooms.length > 1}
				/>
			)}

			{showBathrooms && (shouldShowBedroomsTag || shouldShowRoomsTag) && separador}
			{(showBathrooms && getMinBathooms(bathrooms) > 0) && (
				<BathroomsTag
					isEspecial={isEspecial}
					bathrooms={getMinBathooms(bathrooms)}
					icon={showIcons}
					prefix={Array.isArray(bathrooms) && bathrooms.length > 1}
				/>
			)}

			{showM2 && m2 > 0 && (shouldShowBedroomsTag || shouldShowRoomsTag || showBathrooms) && separador}
			{showM2 && m2 > 0 && (
				<M2Tag
					isEspecial={isEspecial}
					m2={m2}
					hectares={hectares}
					icon={showIcons}
					isMin={Array.isArray(rooms) && rooms.length > 1}
					propertyType={Number(propertyType)}
				/>
			)}

			{showGuests && (shouldShowBedroomsTag || shouldShowRoomsTag || showBathrooms || showM2) && separador}
			{showGuests && <GuestsTag isEspecial={isEspecial} guests={guests} icon={showIcons} />}
		</div>
	);
};

export const TypologyTagSkeleton = ({ className }) => {
	return (
		<div className={"property-typology-tag" + " " + className}>
			<TagSkeleton />
			<TagSkeleton />
			<TagSkeleton />
		</div>
	);
};

export const TagSkeleton = () => {
	return <Skeleton.Button size={"small"} active />;
};
